import { useCallback } from "react";
import { useUserProfileContext } from "~/lib/UserContext";
import { useSupabaseClient } from "~/lib/AuthProvider";
import useSwrFetch from "~/hooks/useSwrFetch";
import PortfolioView from "~/components/portfolio/PortfolioView";
import GetOrgComponent from "~/lib/GetOrgComponent";
import { PluralizedEntityType } from "~/types";

const { getStaticPaths, getStaticProps } = GetOrgComponent("Company");

export { getStaticPaths, getStaticProps };

export default function Portfolio({ staticProjects, staticProfile }) {
  const supabase = useSupabaseClient();

  const visibleProfileFetcher = useCallback(async () => {
    const { data: user, error } = await supabase
      .from(PluralizedEntityType.Companies)
      .select(
        "id, name, slug, logo, linkedin, about, headquarters, legal_status, founded, size, website, cover_image, industries, specialties, headline, twitter, github, opensea, crypto_wallet, instagram, tiktok, facebook, youtube, medium, kaggle, dribbble, behance"
      )
      .eq("slug", staticProfile.slug)
      .eq("org_type", "Company")
      .not("slug", "is", null)
      .single();

    if (user) {
      return user;
    }
  }, [staticProfile.id]);

  const { data: visibleProfile, mutate: mutateVisibleProfile } = useSwrFetch({
    url: `/api/companies/${staticProfile.slug}`,
    fetcher: visibleProfileFetcher,
    fallbackData: staticProfile,
  });

  // Not returning data properly -- fallback is kicking in. Problem appears to be with the filter
  const projectsFetcher = useCallback(async () => {
    const { data, error } = await supabase
      .from("projects_complete")
      .select(
        "id, thumbnail, title, description, type, completed, start_date, companies, contributors_full, contributor_ids, company_ids"
      )
      .or("visibility.eq.portfolio,visibility.eq.showspace")
      .filter("company_ids", "cs", `{${visibleProfile.id}}`)
      .order("completed", { ascending: false });

    if (data) {
      return data;
    }
  }, [visibleProfile.id]);

  const { data: projects } = useSwrFetch({
    url: `/api/projects/companies/${staticProfile.slug}`,
    fetcher: projectsFetcher,
    fallbackData: staticProjects,
  });

  const { profile: logged_in_user } = useUserProfileContext();

  const userIsOwner = visibleProfile.id === logged_in_user?.id;

  const metaTitle =
    visibleProfile.name === undefined
      ? "Showspace Portfolio"
      : `${visibleProfile.name}'s Portfolio`;

  const metaDescription = visibleProfile.intro
    ? visibleProfile.intro
    : visibleProfile.name
    ? `Browse a portfolio of ${visibleProfile.name}'s work.`
    : "Showspace Portfolio";

  let metaImage = null;
  if (visibleProfile && visibleProfile.cover_image !== null) {
    metaImage = `${visibleProfile.cover_image}`;
  } else if (visibleProfile && visibleProfile.logo !== null) {
    metaImage = `${visibleProfile.logo}`;
  } else if (projects[0] && projects[0].thumbnail !== null) {
    metaImage = `https://chmqmeyyaiwfybqgcdoy.supabase.co/storage/v1/object/public/projects/${projects[0].thumbnail}`;
  }

  const metaUrl = `https://showspace.so/company/${visibleProfile.slug}`;

  const onProfileUpdate = useCallback(
    (updatedProfile) => {
      mutateVisibleProfile(updatedProfile);
    },
    [mutateVisibleProfile]
  );

  return (
    <PortfolioView
      userIsOwner={userIsOwner}
      profile={visibleProfile}
      projects={projects}
      onProfileUpdate={onProfileUpdate}
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      metaImage={metaImage}
      metaUrl={metaUrl}
      projectCardLocation="company_profile"
      headingCopy="Featured Portfolio"
      type="org"
      logged_in_user={logged_in_user}
      typeFilter={false}
      sortDropdown={true}
      refPageType="company"
      refPageSlug={visibleProfile.slug}
      refPageLabel={visibleProfile.name}
    />
  );
}
